import { Box, Textarea, SimpleGrid, Text, Flex, MultiSelect } from "@mantine/core";
import {
  getSubIndustriesByIndustry,
  getCollaborationIntentOptions,
  getWhoAmIOptions,
} from "@/components/shared/constants/general.const";

interface BusinessOverviewProps {
  formData: {
    companyDescription: string;
    industryCategory: string[];
    subIndustry: string[];
    whoAmI: string[];
    collaborationIntent: string[];
  };
  updateForm: (field: string, value: any) => void;
  industryOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export default function BusinessOverview({
  formData,
  updateForm,
  industryOptions,
  disabled,
}: BusinessOverviewProps) {
  // Get sub-industry options based on all selected industries (merged + deduplicated)
  const subIndustryOptions =
    formData.industryCategory.length > 0
      ? formData.industryCategory
          .flatMap((ind) => getSubIndustriesByIndustry(ind))
          .filter((opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i)
      : [];

  // Clear sub-industry when industry changes
  const handleIndustryChange = (values: string[]) => {
    updateForm("industryCategory", values);
    updateForm("subIndustry", []); // Reset sub-industries when industry changes
  };

  return (
    <Box>
      {/* Company Description */}
      <Box mb="md">
        <Text fz="sm" fw={500} mb={4}>
          Company Description
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        </Text>
        <Textarea
          placeholder="Describe your company, what you do, your expertise, and what makes you unique..."
          autosize
          minRows={2}
          maxLength={800}
          value={formData.companyDescription}
          onChange={(e) => updateForm("companyDescription", e.currentTarget.value)}
          disabled={disabled}
          error={formData.companyDescription.length > 800 ? "Description cannot exceed 800 characters" : null}
        />
        <Flex justify="space-between" mt={4}>
          <Text fz="xs" c="slate.6">
            Describe your company, expertise, and what makes you unique.
          </Text>
          <Text fz="xs" c="orange">
            {formData.companyDescription.length} / 800 characters
          </Text>
        </Flex>
      </Box>

      {/* Industry Category & Sub-industry */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Industry Category
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          placeholder="Select industries"
          data={industryOptions}
          value={formData.industryCategory || []}
          onChange={handleIndustryChange}
          searchable
          disabled={disabled}
          clearable
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Sub-industry (Optional)
            </Text>
          }
          placeholder={formData.industryCategory.length > 0 ? "Select sub-industries" : "Select industry first"}
          data={subIndustryOptions}
          value={formData.subIndustry || []}
          onChange={(value) => updateForm("subIndustry", value)}
          searchable
          disabled={disabled || formData.industryCategory.length === 0}
          clearable
          maxValues={5}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Who am I?
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          description="Identify your role in the industry"
          placeholder="Select your role"
          data={getWhoAmIOptions()}
          value={formData.whoAmI || []}
          onChange={(value) => updateForm("whoAmI", value)}
          searchable
          disabled={disabled}
          clearable
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Collaboration Intent (Optional)
            </Text>
          }
          description="Select the types of collaboration you're looking for"
          placeholder="Select collaboration types"
          data={getCollaborationIntentOptions()}
          value={formData.collaborationIntent || []}
          onChange={(value) => updateForm("collaborationIntent", value)}
          searchable
          disabled={disabled}
          clearable
          maxValues={5}
        />
      </SimpleGrid>
    </Box>
  );
}
